// @ts-nocheck
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

export const useWarnIfUnsavedChanges = (isDirty: boolean) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const handleConfirmLeave = () => {
    if (pendingNavigation) {
      router.push(pendingNavigation);
      setPendingNavigation(null);
    }
    setShowModal(false);
  };

  const handleCancelLeave = () => {
    setPendingNavigation(null);
    setShowModal(false);
  };

  const handleAnchorClick = (e: MouseEvent) => {
    if (e.button !== 0) return;
    const targetUrl = (e.currentTarget as HTMLAnchorElement).href;
    const currentUrl = window.location.href;

    if (isDirty && targetUrl !== currentUrl) {
      e.preventDefault();
      setPendingNavigation(targetUrl);
      setShowModal(true);
    }
  };

  const addAnchorListeners = () => {
    const anchorElements = document.querySelectorAll('a[href]');
    anchorElements.forEach((anchor: any) => anchor.addEventListener('click', handleAnchorClick));
  };

  useEffect(() => {
    const mutationObserver = new MutationObserver(addAnchorListeners);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    addAnchorListeners();

    return () => {
      mutationObserver.disconnect();
      const anchorElements = document.querySelectorAll('a[href]');
      anchorElements.forEach((anchor: any) => anchor.removeEventListener('click', handleAnchorClick));
    };
  }, [isDirty]);

  useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePopState = () => {
      if (isDirty) {
        const confirmLeave = window.confirm("You have unsaved changes. Do you want to leave?");
        if (!confirmLeave) {
          window.history.pushState(null, '', window.location.href);
        } else {
          window.removeEventListener('popstate', handlePopState);
          window.history.back();
        }
      }
    };

    if (isDirty) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      window.addEventListener('popstate', handlePopState);
      
      window.history.pushState(null, '', window.location.href);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty]);

  useEffect(() => {
    const originalPush = router.push;

    router.push = (url: string, options?: any) => {
      if (isDirty) {
        setPendingNavigation(url);
        setShowModal(true);
      } else {
        originalPush(url, options);
      }
    };

    return () => {
      router.push = originalPush;
    };
  }, [router, isDirty]);

  return { showModal, handleConfirmLeave, handleCancelLeave, setShowModal };
};
